import React, { useState } from 'react';
import { GripVertical } from 'lucide-react';

/**
 * v2 drag-to-rank list — flush rows with 1px dividers, ported from the
 * wizard criteria mock: grip, mono rank badge (top ranks get the cobalt
 * tint — they carry the most weight), stacked label + description. Meant to
 * sit inside a framed card that draws its own border. Reordering also works
 * from the keyboard (arrow keys on a focused row), never drag-only.
 */
export interface RankItem {
  id: string;
  label: string;
  description?: string;
}

export interface RankListProps {
  items: RankItem[];
  onReorder: (next: RankItem[]) => void;
  /** How many top ranks get the cobalt badge tint (default 3). */
  highlightTop?: number;
  /** Viewer cannot reorder: the ranking still reads, the grip and drag go. */
  readOnly?: boolean;
  className?: string;
}

export const RankList: React.FC<RankListProps> = ({
  items,
  onReorder,
  highlightTop = 3,
  readOnly = false,
  className = '',
}) => {
  const [dragIndex, setDragIndex] = useState<number | null>(null);

  const moveTo = (from: number, to: number) => {
    if (from === to) return;
    const next = [...items];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    onReorder(next);
  };

  return (
    <ul className={`flex flex-col ${className}`}>
      {items.map((item, i) => (
        <li
          key={item.id}
          draggable={!readOnly}
          onDragStart={readOnly ? undefined : () => setDragIndex(i)}
          onDragEnd={readOnly ? undefined : () => setDragIndex(null)}
          onDragOver={readOnly ? undefined : (e) => e.preventDefault()}
          onDrop={
            readOnly
              ? undefined
              : () => {
                  if (dragIndex !== null) moveTo(dragIndex, i);
                  setDragIndex(null);
                }
          }
          tabIndex={readOnly ? undefined : 0}
          aria-roledescription={readOnly ? undefined : 'draggable rank item'}
          aria-label={
            readOnly
              ? `${item.label}, rank ${i + 1} of ${items.length}.`
              : `${item.label}, rank ${i + 1} of ${items.length}. Use arrow keys to move.`
          }
          onKeyDown={
            readOnly
              ? undefined
              : (e) => {
                  if (e.key === 'ArrowUp' && i > 0) {
                    e.preventDefault();
                    moveTo(i, i - 1);
                  } else if (e.key === 'ArrowDown' && i < items.length - 1) {
                    e.preventDefault();
                    moveTo(i, i + 1);
                  }
                }
          }
          className={
            'flex items-center gap-s4 border-b border-line-divider px-s6 py-s4 last:border-b-0 ' +
            'transition-colors duration-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-action ' +
            (readOnly
              ? ''
              : 'cursor-grab ' + (dragIndex === i ? 'bg-action-bg' : 'hover:bg-surface-hover'))
          }
        >
          {!readOnly && <GripVertical className="h-[13px] w-[13px] shrink-0 text-line-strong" />}
          <span
            className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-pill font-mono text-[11px] font-bold ${
              i < highlightTop ? 'bg-action-bg text-action-deep' : 'bg-canvas text-ink-muted'
            }`}
          >
            {i + 1}
          </span>
          <span className="flex min-w-0 flex-col">
            <span className="truncate text-row-title text-ink">{item.label}</span>
            {item.description && (
              <span className="truncate text-[11px] text-ink-muted">{item.description}</span>
            )}
          </span>
        </li>
      ))}
    </ul>
  );
};

export default RankList;
